"use client";
import { useRef, useEffect } from "react";
import ReviewDetails from "../current-reviews/ReviewDetails";
import ProductStars from "./ProductStars";
import SvgArrowDown from "./SvgArrowDown";
import { useTranslation } from "@/hooks/localization/useTranslations";
import { Ratings } from "@/api/AppApi/services/home/interfaces/IProduct";
import usePopup from "@/hooks/usePopup";
import { productReviewsPopupAtoms } from "@/hooks/headerHooks";
import { useIsMobile } from "@/hooks/useIsMobile";
import { OverlayTrigger } from "react-bootstrap";
import { useAtom } from "jotai";
import { selectedProductToShowRateAtom } from "../../common-builder/product/product-box/product-box-helpers/product-rating/productBoxRatingAtom";

interface Props {
  rate: number;
  ratings: Ratings;
  productId: number | string;
  canReview: boolean;
}

const RateContextMenu: React.FC<Props> = ({
  rate,
  ratings,
  productId,
  canReview,
}) => {
  const { handleOpenPopup } = usePopup(productReviewsPopupAtoms);
  const [selectedProductToShowRate, setSelectedProductToShowRate] = useAtom(
    selectedProductToShowRateAtom,
  );
  const t = useTranslation();
  const isMobile = useIsMobile();
  const triggerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setSelectedProductToShowRate(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [setSelectedProductToShowRate]);

  const handleTriggerClick = () => {
    setSelectedProductToShowRate((id) => (id === productId ? null : productId));
  };

  return (
    <OverlayTrigger
      show={!isMobile && selectedProductToShowRate === productId}
      trigger={[]}
      placement={"right-start"}
      overlay={
        <div className="rate-context-menu">
          <div className="context-title">{t.product_review}</div>
          <ReviewDetails menu rate={rate} ratings={ratings} />
          <div
            className="see-all-reviews"
            onClick={() => {
              handleOpenPopup({
                product_id: productId,
                rate,
                ratings,
                canReview,
              });
            }}
          >
            {t.see_all_reviews}
          </div>
        </div>
      }
    >
      <div
        ref={triggerRef}
        className="d-flex gap-2"
        style={{ width: "fit-content" }}
        onClick={handleTriggerClick}
      >
        <ProductStars rate={rate} />
        <div className="product-review-count">
          <SvgArrowDown />
          {ratings &&
            Object.values(ratings).reduce((acc, curr) => acc + curr, 0)}
        </div>
      </div>
    </OverlayTrigger>
  );
};

export default RateContextMenu;
